<template>
  <div>
    <!-- Lifetime member toggle -->
    <div class="kb-row">
      <div>
        <h4 class="kb-row-title">
          Lifetime member
        </h4>
        <p class="kb-row-hint">
          Exempt from paying annual dues
        </p>
      </div>
      <div class="kb-row-aside">
        <KbSpinner
          v-if="isUpdatingLifetime"
          label="Saving"
        />
        <ToggleSwitch
          v-model="isLifetime"
          :disabled="isUpdatingLifetime"
          aria-label="Lifetime member"
          @change="onLifetimeStatusChange"
        />
      </div>

      <!-- Lifetime details (shown when lifetime status is active) -->
      <div
        v-if="isLifetime && (member.lifetime_reason || member.lifetime_since)"
        class="kb-row-body"
      >
        <p
          v-if="member.lifetime_reason"
          class="kb-row-note"
        >
          {{ member.lifetime_reason }}
        </p>
        <p
          v-if="member.lifetime_since"
          class="kb-row-meta m-0"
        >
          Lifetime since {{ formatDate(member.lifetime_since) }}
        </p>
      </div>
    </div>

    <!-- Lifetime dialog -->
    <Dialog
      :visible="showLifetimeDialog"
      modal
      header="Grant lifetime status"
      :style="{ width: '28rem' }"
      :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
      :closable="false"
    >
      <div class="kb-form">
        <p class="kb-dialog-lead">
          You're about to grant <strong>{{ member.name || 'this member' }}</strong> lifetime status. Lifetime members don't pay annual dues.
        </p>

        <div class="kb-field">
          <label for="lifetime-reason">Reason</label>
          <Textarea
            id="lifetime-reason"
            v-model="lifetimeReason"
            size="small"
            fluid
            autofocus
            rows="3"
            :invalid="!!lifetimeReasonError"
            aria-describedby="lifetime-reason-error"
            placeholder="Why this member gets lifetime status"
          />
          <small
            v-if="lifetimeReasonError"
            id="lifetime-reason-error"
            class="kb-field-error"
          >{{ lifetimeReasonError }}</small>
        </div>
      </div>

      <template #footer>
        <Button
          label="Cancel"
          text
          severity="secondary"
          @click="cancelLifetime"
        />
        <Button
          label="Grant lifetime status"
          @click="confirmLifetime"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import type { Member } from '~/shared/types/member'
import { formatDate } from '~/utils/dateUtils'

interface Props {
  member: Member
}

const props = defineProps<Props>()

const membersStore = useMembersStore()
const toast = useToast()

// Lifetime status management
const isLifetime = ref(Boolean(props.member.lifetime))
const isUpdatingLifetime = ref(false)
const showLifetimeDialog = ref(false)
const lifetimeReason = ref('')
const lifetimeReasonError = ref('')

// Watch for changes in member data for lifetime status
watch(() => props.member.lifetime, (newValue) => {
  // Explicitly use Boolean conversion for consistency
  isLifetime.value = Boolean(newValue)
}, { immediate: true })

// Lifetime management functions
const onLifetimeStatusChange = () => {
  // If toggling to lifetime, show dialog
  if (isLifetime.value) {
    showLifetimeDialog.value = true
  }
  else {
    // If removing lifetime status, just do it directly
    updateLifetimeStatus(false)
  }
}

const cancelLifetime = () => {
  // Reset UI state
  isLifetime.value = false
  showLifetimeDialog.value = false
  lifetimeReason.value = ''
  lifetimeReasonError.value = ''
}

const confirmLifetime = () => {
  // Validate reason is provided
  if (!lifetimeReason.value.trim()) {
    lifetimeReasonError.value = 'A reason is required when granting lifetime status'
    return
  }

  // Clear error if it was previously set
  lifetimeReasonError.value = ''

  // Close dialog and update lifetime status
  showLifetimeDialog.value = false
  updateLifetimeStatus(true, lifetimeReason.value)
}

const updateLifetimeStatus = async (lifetime: boolean, reason?: string) => {
  isUpdatingLifetime.value = true

  try {
    const success = await membersStore.toggleLifetimeStatus(props.member, lifetime, reason)

    if (success) {
      toast.add({
        severity: 'success',
        summary: lifetime ? 'Lifetime Status Granted' : 'Lifetime Status Removed',
        detail: lifetime
          ? `${props.member.name} has been granted lifetime member status`
          : `${props.member.name} is no longer a lifetime member`,
        life: 3000,
      })
    }
    else {
      // If failed, revert the UI state
      isLifetime.value = !lifetime
      toast.add({
        severity: 'error',
        summary: 'Update Failed',
        detail: `Could not ${lifetime ? 'grant' : 'remove'} lifetime status`,
        life: 3000,
      })
    }
  }
  catch (error) {
    // If error, revert the UI state
    isLifetime.value = !lifetime
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    isUpdatingLifetime.value = false
    lifetimeReason.value = ''
  }
}
</script>
